import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Users, CheckCircle, XCircle, AlertTriangle, Clock, Award, 
  Calendar, TrendingUp, FileText, Plus, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ExaminerCalibrationTab({ token }) {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [selectedExaminer, setSelectedExaminer] = useState(null);
  const [performanceReport, setPerformanceReport] = useState(null);
  const [showCalibrationForm, setShowCalibrationForm] = useState(false);
  const [calibrationForm, setCalibrationForm] = useState({
    examiner_id: '',
    calibration_type: 'annual',
    calibration_date: new Date().toISOString().split('T')[0],
    assessor_id: '',
    assessor_name: '',
    score: 80,
    areas_of_strength: [],
    areas_for_improvement: [],
    recommendations: '',
    passed: true
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchOverview();
  }, [token]); // eslint-disable-line

  const fetchOverview = async () => {
    try {
      const res = await axios.get(`${API}/admin/examiner-calibration-status`, { headers });
      setOverview(res.data);
    } catch (err) {
      toast.error('Failed to load calibration data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPerformanceReport = async (examinerId) => {
    try {
      const res = await axios.get(`${API}/admin/examiner-performance-report/${examinerId}`, { headers });
      setPerformanceReport(res.data);
      setSelectedExaminer(examinerId);
    } catch (err) {
      toast.error('Failed to load performance report');
    }
  };

  const handleSubmitCalibration = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/examiner-calibration`, calibrationForm, { headers });
      toast.success('Calibration recorded successfully');
      setShowCalibrationForm(false);
      fetchOverview();
      setCalibrationForm({
        examiner_id: '',
        calibration_type: 'annual',
        calibration_date: new Date().toISOString().split('T')[0],
        assessor_id: '',
        assessor_name: '',
        score: 80,
        areas_of_strength: [],
        areas_for_improvement: [],
        recommendations: '',
        passed: true
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record calibration');
    }
  };

  const getStatusBadge = (status, isOverdue) => {
    if (isOverdue) return <Badge className="bg-red-100 text-red-700">Overdue</Badge>;
    switch (status) {
      case 'calibrated': return <Badge className="bg-green-100 text-green-700">Calibrated</Badge>;
      case 'requires_remediation': return <Badge className="bg-amber-100 text-amber-700">Needs Remediation</Badge>;
      case 'pending': return <Badge className="bg-blue-100 text-blue-700">Pending</Badge>;
      default: return <Badge className="bg-slate-100">{status}</Badge>;
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-[#1B2A4A] border-t-transparent rounded-full"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="examiner-calibration-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[#1B2A4A]">Examiner Calibration</h3>
          <p className="text-sm text-slate-500">Monitor and manage examiner standards and consistency</p>
        </div>
        <Dialog open={showCalibrationForm} onOpenChange={setShowCalibrationForm}>
          <DialogTrigger asChild>
            <Button className="bg-[#1B2A4A] hover:bg-[#2a3d5f]" data-testid="record-calibration-btn">
              <Plus className="h-4 w-4 mr-2" /> Record Calibration
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Record Calibration Session</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitCalibration} className="space-y-4">
              <div>
                <Label>Examiner</Label>
                <Select value={calibrationForm.examiner_id} onValueChange={(v) => setCalibrationForm({...calibrationForm, examiner_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select examiner" /></SelectTrigger>
                  <SelectContent>
                    {overview?.examiners?.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select value={calibrationForm.calibration_type} onValueChange={(v) => setCalibrationForm({...calibrationForm, calibration_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="initial">Initial</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                      <SelectItem value="remedial">Remedial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={calibrationForm.calibration_date} onChange={(e) => setCalibrationForm({...calibrationForm, calibration_date: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Score (0-100)</Label>
                  <Input type="number" min="0" max="100" value={calibrationForm.score} onChange={(e) => setCalibrationForm({...calibrationForm, score: parseInt(e.target.value), passed: parseInt(e.target.value) >= 70})} />
                </div>
                <div>
                  <Label>Result</Label>
                  <Select value={calibrationForm.passed ? "pass" : "fail"} onValueChange={(v) => setCalibrationForm({...calibrationForm, passed: v === "pass"})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pass">Pass</SelectItem>
                      <SelectItem value="fail">Requires Remediation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Assessor Name</Label>
                <Input value={calibrationForm.assessor_name} onChange={(e) => setCalibrationForm({...calibrationForm, assessor_name: e.target.value})} placeholder="Name of assessor" />
              </div>
              <div>
                <Label>Recommendations</Label>
                <Textarea value={calibrationForm.recommendations} onChange={(e) => setCalibrationForm({...calibrationForm, recommendations: e.target.value})} placeholder="Notes and recommendations..." rows={3} />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowCalibrationForm(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#1B2A4A]">Save Calibration</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold text-[#1B2A4A]">{overview?.calibrated || 0}</p>
                <p className="text-xs text-slate-500">Calibrated</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold text-[#1B2A4A]">{overview?.pending_calibration || 0}</p>
                <p className="text-xs text-slate-500">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-8 w-8 text-red-500" />
              <div>
                <p className="text-2xl font-bold text-[#1B2A4A]">{overview?.overdue_calibration || 0}</p>
                <p className="text-xs text-slate-500">Overdue</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <XCircle className="h-8 w-8 text-amber-500" />
              <div>
                <p className="text-2xl font-bold text-[#1B2A4A]">{overview?.requires_remediation || 0}</p>
                <p className="text-xs text-slate-500">Needs Remediation</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Examiners Table */}
      <Card>
        <CardContent className="p-4">
          <h4 className="font-medium text-[#1B2A4A] mb-4 flex items-center gap-2">
            <Users className="h-4 w-4" /> All Examiners ({overview?.total_examiners || 0})
          </h4>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F8FAFC]">
                  <TableHead className="text-xs font-bold">Examiner</TableHead>
                  <TableHead className="text-xs font-bold">Status</TableHead>
                  <TableHead className="text-xs font-bold">Last Calibration</TableHead>
                  <TableHead className="text-xs font-bold">Score</TableHead>
                  <TableHead className="text-xs font-bold">Next Due</TableHead>
                  <TableHead className="text-xs font-bold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {overview?.examiners?.map((examiner) => (
                  <TableRow key={examiner.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{examiner.name}</p>
                        <p className="text-xs text-slate-500">{examiner.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(examiner.calibration_status, examiner.is_overdue)}</TableCell>
                    <TableCell>
                      {examiner.last_calibration ? (
                        <span className="flex items-center gap-1 text-sm">
                          <Calendar className="h-3 w-3" />
                          {new Date(examiner.last_calibration).toLocaleDateString('en-IN')}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-sm">Never</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {examiner.score ? (
                        <span className={`font-medium ${examiner.score >= 80 ? 'text-green-600' : examiner.score >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                          {examiner.score}%
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {examiner.next_due ? (
                        <span className={`text-sm ${examiner.is_overdue ? 'text-red-600 font-medium' : ''}`}>
                          {new Date(examiner.next_due).toLocaleDateString('en-IN')}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-7 px-2 text-xs"
                          onClick={() => fetchPerformanceReport(examiner.id)}
                          data-testid={`view-report-${examiner.id}`}
                        >
                          <Eye className="h-3 w-3 mr-1" /> Report
                        </Button>
                        <Button 
                          size="sm" 
                          className="h-7 px-2 text-xs bg-[#1B2A4A]"
                          onClick={() => {
                            setCalibrationForm({...calibrationForm, examiner_id: examiner.id});
                            setShowCalibrationForm(true);
                          }}
                        >
                          <Plus className="h-3 w-3 mr-1" /> Calibrate
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Performance Report Modal */}
      {performanceReport && (
        <Dialog open={!!performanceReport} onOpenChange={() => setPerformanceReport(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Performance Report: {performanceReport.examiner?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Examiner Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Email</p>
                  <p className="font-medium">{performanceReport.examiner?.email}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Organization</p>
                  <p className="font-medium">{performanceReport.examiner?.organization || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Calibration Status</p>
                  {getStatusBadge(performanceReport.examiner?.calibration_status)}
                </div>
                <div>
                  <p className="text-xs text-slate-500">Specializations</p>
                  <p className="font-medium">{performanceReport.examiner?.specializations?.join(', ') || '-'}</p>
                </div>
              </div>

              {/* Performance Metrics */}
              <Card>
                <CardContent className="p-4">
                  <h5 className="font-medium mb-3 flex items-center gap-2">
                    <Award className="h-4 w-4" /> Performance Metrics (Last {performanceReport.performance_metrics?.period_days} days)
                  </h5>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-slate-50 rounded">
                      <p className="text-2xl font-bold text-[#1B2A4A]">{performanceReport.performance_metrics?.total_assessments}</p>
                      <p className="text-xs text-slate-500">Total Assessments</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded">
                      <p className="text-2xl font-bold text-[#1B2A4A]">{performanceReport.performance_metrics?.average_score_given}%</p>
                      <p className="text-xs text-slate-500">Avg Score Given</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded">
                      <p className={`text-2xl font-bold ${Math.abs(performanceReport.performance_metrics?.deviation_from_average || 0) > 10 ? 'text-red-600' : 'text-green-600'}`}>
                        {performanceReport.performance_metrics?.deviation_from_average > 0 ? '+' : ''}{performanceReport.performance_metrics?.deviation_from_average}
                      </p>
                      <p className="text-xs text-slate-500">Deviation from Avg</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded">
                      <p className="text-2xl font-bold text-green-600">{performanceReport.performance_metrics?.pass_rate}%</p>
                      <p className="text-xs text-slate-500">Pass Rate</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded">
                      <p className="text-2xl font-bold text-red-600">{performanceReport.performance_metrics?.safety_fail_rate}%</p>
                      <p className="text-xs text-slate-500">Safety Fail Rate</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded">
                      <p className="text-2xl font-bold text-[#1B2A4A]">{performanceReport.performance_metrics?.consistency_score?.toFixed(0)}</p>
                      <p className="text-xs text-slate-500">Consistency Score</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Flags */}
              {Object.values(performanceReport.flags || {}).some(v => v) && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="p-4">
                    <h5 className="font-medium mb-2 flex items-center gap-2 text-amber-700">
                      <AlertTriangle className="h-4 w-4" /> Attention Required
                    </h5>
                    <ul className="space-y-1 text-sm text-amber-800">
                      {performanceReport.flags?.high_deviation && <li>• High scoring deviation from overall average</li>}
                      {performanceReport.flags?.high_fail_rate && <li>• Higher than normal fail rate</li>}
                      {performanceReport.flags?.calibration_overdue && <li>• Calibration is overdue</li>}
                      {performanceReport.flags?.low_feedback_rating && <li>• Low feedback ratings received</li>}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Calibration History */}
              {performanceReport.calibration_history?.length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <h5 className="font-medium mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4" /> Calibration History
                    </h5>
                    <div className="space-y-2">
                      {performanceReport.calibration_history?.slice(0, 5).map((cal, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                          <span>{new Date(cal.calibration_date).toLocaleDateString('en-IN')} - {cal.calibration_type}</span>
                          <div className="flex items-center gap-2">
                            <span className={`font-medium ${cal.score >= 80 ? 'text-green-600' : 'text-amber-600'}`}>{cal.score}%</span>
                            {cal.passed ? <CheckCircle className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-red-500" />}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
